import { useMemo, useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import { NavigationLink, Banner, Button, Drawer, TextField } from '@shared/design-system/ui'
import type { ProductItem } from '../api/admin.api'
import { TechnicalId } from './TechnicalId'
import {
  checkbox,
  confirmation,
  footnote,
  form,
  productMeta,
  productName,
  productSummary,
  stack,
  step,
  stepCopy,
  stepNumber,
  steps,
  stepTitle,
} from './AdminProductEditRequestDrawer.css'

type AdminProductEditRequestDrawerProps = {
  product: ProductItem | null
  producerEmail: string
  producerName?: string
  onClose: () => void
  onApproved: (product: ProductItem, approval: AdminProductEditApproval) => void
}

export type AdminProductEditApproval = {
  approvedBy: string
  reference: string
  approvedAt: string
}

export function AdminProductEditRequestDrawer({
  product,
  producerEmail,
  producerName,
  onClose,
  onApproved,
}: AdminProductEditRequestDrawerProps) {
  const [requestOpened, setRequestOpened] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [reference, setReference] = useState('')
  const [manualProducerEmail, setManualProducerEmail] = useState('')
  const effectiveProducerEmail = producerEmail || manualProducerEmail.trim()

  const mailto = useMemo(() => {
    if (!product || !effectiveProducerEmail) return ''
    const subject = `Autorização para alterar o produto ${product.name}`
    const body = [
      `Olá${producerName ? `, ${producerName}` : ''}.`,
      '',
      `A equipe de gestão precisa realizar uma alteração administrativa no produto “${product.name}”.`,
      `Produto ID: ${product.id}`,
      '',
      'Por favor, responda este e-mail confirmando se autoriza a alteração.',
      '',
      'Nenhuma mudança será feita antes do registro da sua aprovação pela gestão.',
    ].join('\n')
    return `mailto:${encodeURIComponent(effectiveProducerEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }, [effectiveProducerEmail, producerName, product])

  const canContinue = Boolean(
    product && effectiveProducerEmail && confirmed && reference.trim().length >= 3,
  )

  function resetDraft() {
    setRequestOpened(false)
    setConfirmed(false)
    setReference('')
    setManualProducerEmail('')
  }

  function closeDrawer() {
    resetDraft()
    onClose()
  }

  function approveAndOpenEditor() {
    if (!product) return
    resetDraft()
    onApproved(product, {
      approvedBy: effectiveProducerEmail,
      reference: reference.trim(),
      approvedAt: new Date().toISOString(),
    })
  }

  return (
    <Drawer
      open={product !== null}
      onClose={closeDrawer}
      title="Solicitar alteração de produto"
      description="Registre a solicitação e deixe claro o que precisa ser alterado."
      size="standard"
      variant="form"
      dirty={
        requestOpened ||
        confirmed ||
        reference.trim().length > 0 ||
        manualProducerEmail.trim().length > 0
      }
      footer={
        product ? (
          <>
            <Button variant="secondary" onClick={closeDrawer}>
              Cancelar
            </Button>
            <Button
              leftIcon={<ShieldCheck size={16} />}
              disabled={!canContinue}
              onClick={approveAndOpenEditor}
            >
              Registrar aprovação e abrir editor
            </Button>
          </>
        ) : undefined
      }
    >
      {product ? (
        <div className={stack}>
          <div className={productSummary}>
            <h2 className={productName}>{product.name}</h2>
            <TechnicalId label="Produto" value={product.id} />
            <span className={productMeta}>
              Titular: {effectiveProducerEmail || 'e-mail ainda não informado'}
            </span>
          </div>

          <Banner tone="info" title="Edição protegida por confirmação">
            A gestão só abre o editor depois de registrar a aprovação recebida do titular. A
            autorização vale apenas nesta sessão.
          </Banner>

          <ol className={steps}>
            <li className={step}>
              <span className={stepNumber}>1</span>
              <div className={stepCopy}>
                <span className={stepTitle}>Solicite a autorização</span>
                <span>Abra o e-mail já preenchido, revise a mensagem e envie ao titular.</span>
              </div>
            </li>
            <li className={step}>
              <span className={stepNumber}>2</span>
              <div className={stepCopy}>
                <span className={stepTitle}>Aguarde a resposta</span>
                <span>O titular deve responder explicitamente que aprova a alteração.</span>
              </div>
            </li>
            <li className={step}>
              <span className={stepNumber}>3</span>
              <div className={stepCopy}>
                <span className={stepTitle}>Registre a referência e edite</span>
                <span>Informe onde a aprovação ficou registrada antes de abrir o editor.</span>
              </div>
            </li>
          </ol>

          {!producerEmail ? (
            <TextField
              label="E-mail do titular"
              type="email"
              value={manualProducerEmail}
              onChange={(event) => setManualProducerEmail(event.currentTarget.value)}
              placeholder="titular@empresa.com"
              helperText="Seu perfil não pode consultar usuários. Confirme o responsável pelo produto antes de enviar."
            />
          ) : null}

          {effectiveProducerEmail ? (
            <NavigationLink href={mailto} onClick={() => setRequestOpened(true)}>
              Abrir solicitação por e-mail
            </NavigationLink>
          ) : null}

          {requestOpened ? (
            <Banner tone="success" title="Solicitação aberta no aplicativo de e-mail">
              Confirme o envio por lá e só continue depois de receber a resposta do titular.
            </Banner>
          ) : null}

          <div className={form}>
            <TextField label="Aprovado por" value={effectiveProducerEmail} readOnly />
            <TextField
              label="Referência da aprovação"
              value={reference}
              onChange={(event) => setReference(event.currentTarget.value)}
              placeholder="Ex.: resposta por e-mail em 20/08 às 14:30"
              helperText="Use uma referência que a equipe consiga localizar depois."
            />
            <label className={confirmation}>
              <input
                className={checkbox}
                type="checkbox"
                checked={confirmed}
                onChange={(event) => setConfirmed(event.currentTarget.checked)}
              />
              <span>
                Confirmo que recebi uma aprovação explícita do titular deste produto para realizar a
                alteração administrativa.
              </span>
            </label>
          </div>

          <p className={footnote}>
            Ao salvar o produto, a API administrativa registra o usuário da gestão, a ação, o
            horário e os dados alterados. Esta confirmação operacional não substitui esse log.
          </p>
        </div>
      ) : null}
    </Drawer>
  )
}
